import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  AnvaQuote,
  AnvaQuoteWithJob,
  AnvaJobStatus,
  AnvaInsuranceCompany,
  AnvaInsurance,
  AnvaCoverage,
  AnvaDocument,
  AnvaPossiblePackage,
  AnvaWarning,
} from '@flows/src/types/anva';
import {
  useAnvaQuotesV2,
  getJobResult,
  useCompletedQuotesCache,
  isAnvaQuote,
  isJobErrorResult,
} from '@flows/src/api/anva-flows/getAnvaQuotesV2';
import { AnvaFlowsInsuranceTag } from '@flows/src/api/anva-flows/getAnvaFlowsInsuranceTags';
import { groupAnvaQuotes } from '@flows/src/utils/anvaQuoteGrouping';
import {
  createAnvaProductsNavigationMap,
  FlowProductsNavigationMap,
} from '@flows/src/hooks/flow-products/createSidebarNavigationMap';

interface UseAnvaQuotesWithPollingParams {
  sessionId?: string;
  enabled?: boolean;
}

interface QuoteWithJobState extends Omit<AnvaQuote, 'id' | 'index'> {
  anva_product_id: string;
  name: string;
  insurance_company: AnvaInsuranceCompany;
  insurance: AnvaInsurance;
  tag: Omit<AnvaFlowsInsuranceTag, 'insurance_ids'>;
  jobId: string;
  jobStatus: AnvaJobStatus;
  isLoading: boolean;
  total_premium: number;
  payment_interval: string | null;
  coverages: AnvaCoverage[];
  documents: AnvaDocument[];
  possible_packages: AnvaPossiblePackage[];
  success: boolean;
  error: string | null;
  // Optional fields that may come from job results
  id?: string;
  index?: number;
  anva_collection_method_code?: number | null;
  start_date?: string;
  warnings?: AnvaWarning[];
}

export const useAnvaQuotesWithPolling = ({
  sessionId,
  enabled = true,
}: UseAnvaQuotesWithPollingParams) => {
  // Attempt to read any previously cached completed quotes synchronously so we
  // can decide *during the first render* whether we already have all the data
  // we need.  If we find cached quotes we directly use them as the initial
  // state and skip the initial "/calculate" API call – preventing an
  // unnecessary recalculation when navigating back from the basket to the
  // overview page.

  const { getCachedQuotes, setCachedQuotes, clearCachedQuotes } =
    useCompletedQuotesCache(sessionId);

  // Read the cache once. We store it in a ref so that this lookup only happens
  // on the very first render and stays referentially stable across re-renders.
  const cachedQuotesRef = useRef<AnvaQuote[] | null>(null);
  if (cachedQuotesRef.current === null) {
    const fetchedCachedQuotes = getCachedQuotes();
    // Normalize undefined to null for consistent typing.
    cachedQuotesRef.current = fetchedCachedQuotes ?? null;
  }

  // Convert cached quotes (if any) to the internal QuoteWithJobState format
  // that this hook uses. This logic mirrors what previously happened inside
  // the useEffect block.
  const cachedQuoteStates: QuoteWithJobState[] =
    cachedQuotesRef.current?.map((quote, index) => ({
      ...quote,
      error: quote.error ?? null, // Handle undefined -> null conversion
      jobId: `cached-${quote.anva_product_id}-${index}`,
      jobStatus: 'SUCCEEDED' as AnvaJobStatus,
      isLoading: false,
    })) || [];

  // Create navigation map from cached quotes if available
  const cachedNavigationMap: FlowProductsNavigationMap | null = useMemo(() => {
    if (cachedQuotesRef.current && cachedQuotesRef.current.length > 0) {
      return createAnvaProductsNavigationMap(cachedQuotesRef.current);
    }
    return null;
  }, []);

  // Initialise the component state from the cache (if present).
  const [quotes, setQuotes] = useState<QuoteWithJobState[]>(cachedQuoteStates);
  const [hasInitialized, setHasInitialized] = useState(
    cachedQuoteStates.length > 0,
  );
  const [navigationMap, setNavigationMap] =
    useState<FlowProductsNavigationMap | null>(cachedNavigationMap);

  const pollTimeoutRef = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );
  const pollStartTimeRef = useRef<Record<string, number>>({});

  const POLL_INTERVAL = 3000; // 3 seconds
  const POLL_TIMEOUT = 180000; // 3 minutes

  // Initial v2 API call to get jobs
  const {
    data: initialData,
    isLoading: isLoadingInitial,
    isError,
    error,
  } = useAnvaQuotesV2({
    sessionId,
    queryOptions: {
      // Skip the initial API call when we already have cached quotes.
      enabled: enabled && !!sessionId && !hasInitialized,
      onSuccess: (data) => {
        if (data?.quotes) {
          initializeQuotesWithJobs(data.quotes);
          // create navigation map from initial job quotes
          const navMap = createAnvaProductsNavigationMap(data.quotes);
          setNavigationMap(navMap);
          setHasInitialized(true);
        }
      },
    },
  });

  const initializeQuotesWithJobs = useCallback(
    (jobQuotes: AnvaQuoteWithJob[]) => {
      const initialQuotes: QuoteWithJobState[] = jobQuotes.map((jobQuote) => ({
        anva_product_id: jobQuote.anva_product_id,
        name: jobQuote.name,
        insurance_company: jobQuote.insurance_company,
        insurance: jobQuote.insurance,
        tag: jobQuote.tag,
        index: jobQuote.index,
        jobId: jobQuote.job.job_id,
        jobStatus: jobQuote.job.status,
        isLoading: true,
        total_premium: 0,
        payment_interval: null,
        // this is kind of wrong but we are just waiting on the job to finish
        coverages:
          jobQuote.coverages?.map((coverage) => ({
            ...coverage,
            selected: true,
          })) || [],
        // coverages: [],
        documents: [],
        possible_packages: [],
        success: false,
        error: null,
      }));

      setQuotes(initialQuotes);

      // Start polling for each job
      initialQuotes.forEach((quote) => {
        startPolling(quote.jobId);
      });
    },
    [],
  );

  const pollJobResult = useCallback(async (jobId: string) => {
    try {
      const jobResult = await getJobResult(jobId);

      // Check if job is completed (succeeded or failed)
      if (jobResult.status === 'SUCCEEDED' || jobResult.status === 'FAILED') {
        if (
          jobResult.status === 'SUCCEEDED' &&
          jobResult.result &&
          isAnvaQuote(jobResult.result)
        ) {
          handleQuoteResultSuccess(jobId, jobResult.result);
        } else {
          // Extract error details from job result if available
          let errorMessage = 'NO_RESULT_AVAILABLE';

          if (jobResult.status === 'FAILED') {
            if (jobResult.result && isJobErrorResult(jobResult.result)) {
              errorMessage =
                jobResult.result.message ||
                jobResult.result.title ||
                JSON.stringify(jobResult);
            } else {
              errorMessage = JSON.stringify(jobResult);
            }
          }

          handleQuoteResultError(jobId, errorMessage);
        }
        stopPolling(jobId);
        return true; // Job completed
      }

      // Job is still in progress (QUEUED or RUNNING)
      return false; // Continue polling
    } catch (error) {
      console.error(`Error polling job ${jobId}:`, error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      handleQuoteResultError(jobId, errorMessage);
      stopPolling(jobId);
      return true; // Stop polling on error
    }
  }, []);

  const handleQuoteResultSuccess = useCallback(
    (jobId: string, result: AnvaQuote) => {
      // update quotes
      setQuotes((prevQuotes) =>
        prevQuotes.map((quote) =>
          quote.jobId === jobId
            ? {
                ...quote,
                ...result,
                jobStatus: 'SUCCEEDED' as AnvaJobStatus,
                isLoading: false,
              }
            : quote,
        ),
      );

      // update navigation map with risk_object_name when available
      const riskObjectName = result.risk_object_name;
      if (riskObjectName) {
        setNavigationMap((prevNavigationMap) => {
          if (!prevNavigationMap) {
            return prevNavigationMap;
          }

          return prevNavigationMap.map((item) => {
            if (item.key === result.tag.id.toString()) {
              // If there are subItems, update the specific subItem's label
              if (item.subItems && result.index !== undefined) {
                const updatedSubItems = item.subItems.map((subItem) =>
                  subItem.index === result.index
                    ? { ...subItem, label: riskObjectName }
                    : subItem,
                );
                return { ...item, subItems: updatedSubItems };
              }
            }
            return item;
          });
        });
      }
    },
    [],
  );

  const handleQuoteResultError = useCallback(
    (jobId: string, errorMessage: string) => {
      setQuotes((prevQuotes) =>
        prevQuotes.map((quote) =>
          quote.jobId === jobId
            ? {
                ...quote,
                jobStatus: 'FAILED' as AnvaJobStatus,
                isLoading: false,
                success: false,
                error: errorMessage,
              }
            : quote,
        ),
      );
    },
    [],
  );

  const startPolling = useCallback(
    (jobId: string) => {
      // Clear any existing timeout
      if (pollTimeoutRef.current[jobId]) {
        clearTimeout(pollTimeoutRef.current[jobId]);
      }

      // Record start time
      pollStartTimeRef.current[jobId] = Date.now();

      const poll = async () => {
        try {
          const elapsed = Date.now() - pollStartTimeRef.current[jobId];

          // Check timeout
          if (elapsed >= POLL_TIMEOUT) {
            handleQuoteResultError(jobId, 'CALCULATION_TIMEOUT');
            stopPolling(jobId);
            return;
          }

          const completed = await pollJobResult(jobId);

          if (!completed) {
            // Validate refs are still active before scheduling next poll
            if (pollTimeoutRef.current && pollStartTimeRef.current[jobId]) {
              // Schedule next poll
              pollTimeoutRef.current[jobId] = setTimeout(poll, POLL_INTERVAL);
            }
          }
        } catch (error) {
          // Handle any unhandled exceptions in the polling loop
          console.error(
            `Critical error in polling loop for job ${jobId}:`,
            error,
          );
          const errorMessage =
            error instanceof Error ? error.message : 'POLLING_LOOP_ERROR';
          handleQuoteResultError(jobId, errorMessage);
          stopPolling(jobId);
        }
      };

      // Start first poll immediately with error handling
      poll().catch((error) => {
        console.error(`Error starting poll for job ${jobId}:`, error);
        const errorMessage =
          error instanceof Error ? error.message : 'POLL_START_ERROR';
        handleQuoteResultError(jobId, errorMessage);
        stopPolling(jobId);
      });
    },
    [pollJobResult, handleQuoteResultError],
  );

  const stopPolling = useCallback((jobId: string) => {
    if (pollTimeoutRef.current[jobId]) {
      clearTimeout(pollTimeoutRef.current[jobId]);
      delete pollTimeoutRef.current[jobId];
    }
    delete pollStartTimeRef.current[jobId];
  }, []);

  // Cleanup on unmount and add ref validation
  useEffect(() => {
    return () => {
      Object.keys(pollTimeoutRef.current).forEach((jobId) => {
        stopPolling(jobId);
      });
    };
  }, [stopPolling]);

  // Additional safeguard to detect stuck polling jobs
  useEffect(() => {
    const checkStuckJobs = () => {
      const now = Date.now();
      Object.entries(pollStartTimeRef.current).forEach(([jobId, startTime]) => {
        const elapsed = now - startTime;
        if (elapsed > POLL_TIMEOUT + 30000) {
          // 30 seconds grace period
          console.warn(`Detected stuck polling job ${jobId}, cleaning up...`);
          stopPolling(jobId);
          handleQuoteResultError(jobId, 'POLLING_STUCK_CLEANUP');
        }
      });
    };

    const cleanupInterval = setInterval(checkStuckJobs, 30000); // Check every 30 seconds
    return () => clearInterval(cleanupInterval);
  }, [stopPolling, handleQuoteResultError]);

  // Only show global loading if we haven't initialized quotes yet
  const isLoading = isLoadingInitial && !hasInitialized;

  // Convert quotes to the expected format for the UI
  const formattedQuotes: AnvaQuote[] = quotes.map((quote, index) => ({
    id: quote.id || `${quote.anva_product_id}-${index}`,
    index: quote.index ?? -1, // Use -1 as placeholder until API returns proper index
    anva_product_id: quote.anva_product_id,
    name: quote.name,
    risk_object_name: quote.risk_object_name,
    insurance_company: quote.insurance_company,
    insurance: quote.insurance,
    tag: quote.tag,
    total_premium: quote.total_premium,
    payment_interval: quote.payment_interval,
    coverages: quote.coverages,
    documents: quote.documents,
    possible_packages: quote.possible_packages,
    success: quote.success,
    error: quote.error,
    anva_collection_method_code: quote.anva_collection_method_code,
    start_date: quote.start_date,
    warnings: quote.warnings,
    jobStatus: quote.jobStatus,
    jobId: quote.jobId,
    isLoading: quote.isLoading,
  }));

  // Cache completed quotes when all polling is done
  useEffect(() => {
    const allCompleted =
      quotes.length > 0 && quotes.every((quote) => !quote.isLoading);
    if (allCompleted) {
      setCachedQuotes(formattedQuotes);
    }
  }, [quotes, formattedQuotes, setCachedQuotes]);

  // Function to clear initialization state and allow recalculation
  const clearInitialization = useCallback(() => {
    setHasInitialized(false);
    setQuotes([]);
    setNavigationMap(null);

    // Clear any active polling
    Object.keys(pollTimeoutRef.current).forEach((jobId) => {
      stopPolling(jobId);
    });

    // Clear cached completed quotes to force fresh calculation
    clearCachedQuotes();
  }, [stopPolling, clearCachedQuotes]);

  // Update quote (after recalculation which does not use jobs/polling)
  const updateQuote = useCallback(
    (updatedQuote: AnvaQuote, originalJobId: string) => {
      setQuotes((prevQuotes) =>
        prevQuotes.map((quote) => {
          if (quote.jobId === originalJobId) {
            return {
              ...quote,
              ...updatedQuote,
              jobStatus: 'SUCCEEDED' as AnvaJobStatus,
              error: updatedQuote.error ?? null,
            };
          }
          return quote;
        }),
      );

      // Skipping, update navigation map with risk_object_name
      // niche scenario, will be gone after react-query implementation
    },
    [],
  );

  // Set loading state for a specific quote by jobId
  const setLoading = useCallback((jobId: string, isLoading: boolean) => {
    setQuotes((prevQuotes) =>
      prevQuotes.map((quote) => {
        if (quote.jobId === jobId) {
          return {
            ...quote,
            isLoading,
          };
        }
        return quote;
      }),
    );
  }, []);

  return {
    data: {
      quotes: formattedQuotes,
      packages: initialData?.packages || [],
      groupingResult: groupAnvaQuotes(formattedQuotes),
      navigationMap,
    },
    isLoading,
    isError,
    error,
    refetch: clearInitialization,
    clearInitialization, // Expose this for external use if needed
    updateQuote,
    setLoading,
  };
};
